import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import usePresence from './usePresence';
import { readCssDurationMs } from '../utils/cssDuration';

const FOCUSABLE = 'input:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/* The exit runs at 60% of the entrance (--input-transition-duration) — the
   panel answers the dismissal at once. Read at call time: brand-scoped. */
const exitMs = () => Math.round(readCssDurationMs('--input-transition-duration', 250) * 0.6);

/**
 * FilterPopover — the desktop panel under a Filter trigger (internal).
 *
 * Non-modal dialog, rendered inside the trigger's item wrapper (its parent
 * element is the anchor). On open it moves focus to its first control. It
 * closes on a press outside the anchor, on Escape and when focus leaves the
 * anchor; `onClose(reason)` gets 'outside' | 'escape' | 'blur' so the owner
 * can return focus on Escape. The skin is the DS Input dropdown's
 * (--input-dropdown-*), see Filter.css.
 */
const FilterPopover = ({ id, open, label, align = 'start', variant = 'list', onClose, children }) => {
  const { mounted, closing } = usePresence(open, exitMs);
  const panelRef = useRef(null);
  const [flipped, setFlipped] = useState(false);

  // The latest onClose without re-subscribing the document listeners per render
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!open || !panel) return;
    panel.querySelector(FOCUSABLE)?.focus({ preventScroll: true });
    // A start-aligned panel that would run off the right edge opens leftwards
    const rect = panel.getBoundingClientRect();
    const viewWidth = (panel.ownerDocument.defaultView || window).innerWidth;
    setFlipped(align === 'start' && rect.width > 0 && rect.right > viewWidth);
  }, [open, align]);

  useEffect(() => {
    if (!open) return undefined;
    // The panel's own document — a preview iframe has its own
    const doc = panelRef.current?.ownerDocument || document;
    const anchor = () => panelRef.current?.parentElement;
    const onPointerDown = (e) => {
      const el = anchor();
      if (el && !el.contains(e.target)) onCloseRef.current?.('outside');
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.('escape');
    };
    doc.addEventListener('mousedown', onPointerDown);
    doc.addEventListener('keydown', onKeyDown);
    return () => {
      doc.removeEventListener('mousedown', onPointerDown);
      doc.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Tabbing out closes it; a null relatedTarget (a click on nothing focusable,
  // Safari not focusing a clicked checkbox) is left to the outside press.
  const handleBlur = (e) => {
    const anchor = panelRef.current?.parentElement;
    const next = e.relatedTarget;
    if (open && anchor && next && !anchor.contains(next)) onCloseRef.current?.('blur');
  };

  if (!mounted) return null;

  const end = align === 'end' || flipped;
  const className = [
    'ds-filter__panel',
    `ds-filter__panel--${variant}`,
    end && 'ds-filter__panel--end',
    closing && 'ds-filter__panel--closing',
  ].filter(Boolean).join(' ');

  return (
    <div id={id} ref={panelRef} role="dialog" aria-label={label} className={className} onBlur={handleBlur}>
      {children}
    </div>
  );
};

FilterPopover.propTypes = {
  id: PropTypes.string.isRequired,
  open: PropTypes.bool,
  /** The dialog's accessible name — the trigger's label */
  label: PropTypes.string.isRequired,
  /** 'start' opens under the trigger's left edge (flips when it would overflow); 'end' under its right edge */
  align: PropTypes.oneOf(['start', 'end']),
  /** 'list' (options) | 'range' (fixed width) */
  variant: PropTypes.oneOf(['list', 'range']),
  onClose: PropTypes.func,
  children: PropTypes.node,
};

export default FilterPopover;
